/**
 * `Tooltip` (W1-12 ui-kit-i18n; contract TooltipProps): Radix tooltip that opens on hover (shared delay) and on
 * keyboard focus, closes on Escape, and appends the shortcut of `command` (first binding, via the UiBridge).
 * "Keyboard focus" means focus moved by Tab / arrows (internal/focusIntent.ts): focus that a dialog or popover
 * moves on open, that a closing layer gives back to its trigger, or that follows a pointer press opens no
 * tooltip, so it never takes the next Escape away from the dialog or the editor.
 * The trigger is the child element itself (it must accept a ref and DOM props; plain text is wrapped in a
 * focusable span). Disabled controls keep using aria-disabled so their tooltip (with the reason) still shows.
 * A Button or IconButton child is not wrapped: it receives the content and shows it in its own tooltip, so its
 * command still runs on click and one bubble carries the content, the shortcut and any disabled reason (see
 * internal/commandButton.ts).
 */

import * as RT from '@radix-ui/react-tooltip';
import { cloneElement, isValidElement, type ReactNode } from 'react';
import { firstChord, TOOLTIP_DELAY_MS, TOOLTIP_SKIP_DELAY_MS, useHasTooltipProvider, useUiBridge } from './bridge';
import { isCommandButtonElement } from './internal/commandButton';
import { focusFromKeyboardNavigation } from './internal/focusIntent';
import { Kbd } from './Kbd';
import s from './Tooltip.module.css';
import type { TooltipProps } from './types';

export interface TooltipExtraProps {
  /** Second line under the content (e.g. why a control is disabled). */
  readonly detail?: ReactNode;
  /** Open state for controlled use (tests, tours). */
  readonly open?: boolean | undefined;
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
}

export type TooltipAllProps = Omit<TooltipProps, 'command' | 'side'> &
  TooltipExtraProps & {
    readonly command?: string | undefined;
    readonly side?: TooltipProps['side'] | undefined;
  };

export function Tooltip(props: TooltipAllProps) {
  const bridge = useUiBridge();
  const hasProvider = useHasTooltipProvider();
  const child = props.children;
  if (isCommandButtonElement(child)) {
    return cloneElement(child, {
      tooltip: {
        content: props.content,
        command: props.command,
        side: props.side,
        detail: props.detail,
        open: props.open,
        onOpenChange: props.onOpenChange,
      },
    });
  }
  const chord = firstChord(bridge, props.command);
  const trigger = isValidElement(child) ? (
    child
  ) : (
    <span tabIndex={0} className={s.textTrigger}>
      {child}
    </span>
  );
  const rootProps = {
    ...(props.open === undefined ? {} : { open: props.open }),
    ...(props.onOpenChange === undefined
      ? {}
      : {
          onOpenChange: (open: boolean) => {
            props.onOpenChange?.(open);
          },
        }),
  };
  const tooltip = (
    <RT.Root {...rootProps}>
      <RT.Trigger
        asChild
        onFocus={(e) => {
          // Radix opens on every focus that did not come from a pointer press; keep it to keyboard navigation.
          if (!focusFromKeyboardNavigation()) e.preventDefault();
        }}
      >
        {trigger}
      </RT.Trigger>
      <RT.Portal>
        <RT.Content className={s.content} side={props.side ?? 'top'} sideOffset={6} collisionPadding={8}>
          <span className={s.main}>
            <span className={s.text}>{props.content}</span>
            {chord !== undefined && <Kbd chord={chord} className={s.kbd} />}
          </span>
          {props.detail !== undefined && props.detail !== null && <span className={s.detail}>{props.detail}</span>}
        </RT.Content>
      </RT.Portal>
    </RT.Root>
  );
  if (hasProvider) return tooltip;
  return (
    <RT.Provider delayDuration={TOOLTIP_DELAY_MS} skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}>
      {tooltip}
    </RT.Provider>
  );
}
